import Translator from "@/app/services/Translator";
import { Alpine } from "@/livewire";
import { Livewire as LivewireDefinition } from '@nolikein/types-livewire3';

/**
 * Determine if the app is running in production mode.
 */
export function isProductionMode(): boolean {
    return import.meta.env.PROD;
}

/**
 * Determine if the app is running in development mode.
 */
export function isDevelopmentMode(): boolean {
    return import.meta.env.DEV;
}

/**
 * Retrieve the values of an enumerator as an array of strings.
 * @param enumerator The enumerator object.
 * @returns An array of string values.
 */
export function getEnumValues(enumerator: object): Array<string> {
    return Object.keys(enumerator).filter((item) => {
        return isNaN(Number(item));
    });
}

/**
 * Translate a message
 * @param message
 * @param params
 */
export function translate(message: string, params: Record<string, string> = {}): string {
    const translator = new Translator();
    translator.init();

    return translator.translate(message, params);
}

/**
 * Perform a clone of a string.
 *
 * Since Javascript use references everywhere, it is better
 * to clone a string intented to be reused.
 * @param originalString
 */
export function copyString(originalString: string) {
    return (' ' + originalString).slice(1);
}

/**
 * Raw the content of an Alpinejs component
 * @param obj
 */
export function raw<Type>(obj: Type): Type {
    return Alpine.raw<Type>(obj);
}

/**
 * Retrieve the nearest livewire component or null
 * @param el
 */
export function nearestLivewireComponent(el: HTMLElement): LivewireDefinition.LivewireHTMLElement | null {
    const component = Alpine.findClosest(el, (i: HTMLElement) => "__livewire" in i);
    if (!component) {
        return null;
    }

    return component as LivewireDefinition.LivewireHTMLElement;
}

type Listener = (customEvent: CustomEvent) => void;

/**
 * Listen an event from window.
 */
export function listen(event: string, listener: Listener): () => void {
    window.addEventListener(event, (listener) as EventListener);

    return (() => window.removeEventListener(event, (listener) as EventListener));
}

/**
 * Execute event listeners on page unload
 */
export function unlistenOnUnload(listeners: Array<() => void>): void {
    window.addEventListener('onbeforeunload', () => {
        listeners.forEach(listener => {
            listener();
        });
    });
}

/**
 * Dispatch an event to window
 */
export function dispatch<T>(eventName: string, data: T | undefined = undefined) {
    let customEventInit = undefined;

    if (data !== undefined) {
        customEventInit = {
            detail: data,
        };
    }
    window.dispatchEvent(
        new CustomEvent(eventName, customEventInit),
    );
}

export interface NavigateEvent {
    /** A URL object of the intended destination of the navigation */
    url: URL,
    /** A boolean [true/false] indicating whether or not this navigation was triggered by a back/forward (history state) navigation */
    history: boolean,
    /** A boolean [true/false] indicating whether or not there is cached version of this page to be used instead of fetching a new one via a network round-trip */
    cached: boolean,
}

export function baseName(str: string) {
    let base = new String(str).substring(str.lastIndexOf('/') + 1);
    if (base.lastIndexOf(".") != -1) {
        base = base.substring(0, base.lastIndexOf("."));
    }
    return base;
}

/**
 * Retrieve the csrf token from the header section.
 */
export function csrfToken(): string | null {
    return document.querySelector<HTMLElement>('meta[name="csrf-token"]')?.getAttribute('content') ?? null;
}

/**
 * Retrieve the user id from the header section.
 */
export function getUserId(): string | null {
    return document.querySelector<HTMLElement>('meta[name="user-id"]')?.getAttribute('content') ?? null;
}

/**
 * Determines whether the user is logged in
 */
export function amILoggedIn(): boolean {
    return null !== csrfToken() && null !== getUserId();
};

/**
 * Add a notification to the menu (this is used by the server-sent events to add notifications in real time without refreshing the page).
 * @param id The notification id (used to mark it as read later)
 * @param message The notification message (already translated by the server)
 * @param created_at_for_humans The creation date of the notification in a human readable format (e.g. "5 minutes ago", "yesterday", etc.)
 * @param read_at The read date of the notification in ISO format, or null if the notification is unread (used to display it differently in the menu)
 */
export function addNotificationToMenu(id: string, message: string, created_at_for_humans: string, read_at: string | null) {
    dispatch('add-user-notification', {
        id,
        message,
        created_at_for_humans,
        read_at,
    });
};

// Escape map for HTML entities (shared across calls)
const HTML_ESCAPE_MAP: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

/**
 * Sanitize a string by escaping HTML special characters, while allowing certain tags to be preserved.
 */
export function sanitizeHtmlTag(text: string, options: { allow: Array<string> } = { allow: [] }): string {
    // If no allowed tags, escape everything using simple character iteration
    if (0 === options.allow.length) {
        let result = '';
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            result += HTML_ESCAPE_MAP[char] ?? char;
        }
        return result;
    }

    // For allowed tags, regex is still the most practical solution
    const allowedTagsPattern = options.allow.join('|');
    const regex = new RegExp(`<(/?\\s*(${allowedTagsPattern})(\\s+[^>]*?)?)>`, 'gi');

    const placeholders: Map<string, string> = new Map();
    let placeholderIndex = 0;

    const sanitizedText = text.replace(regex, (match) => {
        // Utilise le caractère NULL impossible dans HTML
        const placeholder = `\x00PH${placeholderIndex}\x00`;
        placeholders.set(placeholder, match);
        placeholderIndex++;
        return placeholder;
    }).replace(/[&<>"']/g, (c) => HTML_ESCAPE_MAP[c]);

    if (0 === placeholders.size) {
        return sanitizedText;
    }

    // eslint-disable-next-line no-control-regex
    return sanitizedText.replace(/\x00PH\d+\x00/g, (placeholder) => {
        return placeholders.get(placeholder) ?? placeholder;
    });
}

/**
 * Merge two objects into a new one. This is a shallow merge, only the first level of properties is merged.
 */
export function mergeObject(obj1: object, obj2: object) {
    return {
        ...obj1,
        ...obj2,
    };
}

/**
 * Checks if a value is a valid float
 *
 * @param value Is the value a valid float ?
 */
export function isValidFloat(value: number): boolean {
    return !isNaN(value);
}

/**
 * Ensure a value use the float "." notation
 * @param value
 * @returns String
 */
export function forceEnglishFloatNotation(value: string): string {
    return value.replace(",", ".");
}
